import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import type { Role } from '@cfl/shared';

import { api, ApiError } from './api';
import { setOrganizationTimeZone } from './today';

export const SESSION_QUERY_KEY = ['auth', 'me'] as const;

export interface Session {
  id: string;
  email: string;
  displayName: string;
  alias: string | null;
  avatarUrl: string | null;
  role: Role;
  timezone: string;
  /** The step a duration has to sit on to be accepted. Drives the preset lists. */
  durationIncrementMinutes: number;
  /** `HH:MM`. What a new entry starts at unless somebody types otherwise. */
  defaultBeginTime: string;
  /** IANA zone that decides which calendar day "today" is, for everybody. */
  organizationTimezone: string;
}

/**
 * Who is signed in, or null.
 *
 * A 401 is a legitimate answer here - "nobody is signed in" - not an error, so it
 * resolves to null and is never retried. Retrying it would delay the sign-in page
 * behind three failing requests.
 */
export function useSession() {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async (): Promise<Session | null> => {
      try {
        const session = await api.get<Session>('/auth/me');
        setOrganizationTimeZone(session.organizationTimezone);
        return session;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useSignOut() {
  return React.useCallback(async () => {
    await api.post('/auth/logout').catch(() => undefined);

    // Tell Google to stop handing this account straight back. Without it, Identity
    // Services remembers the last account and can re-authenticate silently on the
    // sign-in page - signing the person back in immediately after they asked not
    // to be. Optional-chained: the script is absent when Google sign-in is off.
    (window as unknown as { google?: GoogleIdentity }).google?.accounts?.id?.disableAutoSelect?.();

    // A real navigation, not a client-side route change.
    //
    // This is what makes sign-out actually happen on the click. The previous
    // attempt cleared the query cache and wrote null over the session, and the
    // screen still showed the old one until the tab was reloaded by hand -
    // queryClient.clear() DESTROYS the query a mounted observer is bound to, and
    // the setQueryData after it creates a new query that observer never re-binds
    // to, so the component goes on rendering its last result. Verified against
    // query-core rather than reasoned about, after getting it wrong once.
    //
    // Reloading also does, properly, what clear() was there for: every cached
    // response, and anything else this tab was holding, is gone. On a shared
    // machine that is the point.
    window.location.assign('/login');
  }, []);
}

/** Only the part of Google Identity Services that sign-out needs. */
interface GoogleIdentity {
  accounts?: { id?: { disableAutoSelect?: () => void } };
}
